import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";
import { useSEO } from "@/components/Seo";
import { eyebrow } from "@/lib/styles";

export default function Shop() {
  useSEO(
    "Shop the Collection | Threads Unbound™",
    "Wearable art and prints from Threads Unbound™. Heavyweight tees, signed giclée prints, and studio pieces, fulfilled through our Etsy studio."
  );
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!products.length) return;
    const priceOf = (p) => {
      const m = (p.price || "").match(/[\d.]+/);
      return m ? m[0] : undefined;
    };
    const items = products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.title,
        description: p.description,
        ...(p.image_url ? { image: new URL(p.image_url, window.location.origin).href } : {}),
        brand: { "@type": "Brand", name: "Threads Unbound™" },
        ...(p.etsy_url
          ? {
              offers: {
                "@type": "Offer",
                url: p.etsy_url,
                priceCurrency: "USD",
                ...(priceOf(p) ? { price: priceOf(p) } : {}),
                availability: "https://schema.org/InStock",
              },
            }
          : {}),
      },
    }));
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "shop-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Threads Unbound Shop",
      itemListElement: items,
    });
    document.head.appendChild(script);
    return () => document.getElementById("shop-jsonld")?.remove();
  }, [products]);

  return (
    <main className="px-6 pb-32 pt-36 md:px-12 md:pt-44 lg:px-20" data-testid="shop-page">
      <header>
        <MaskedLine>
          <span className={eyebrow}>The collection</span>
        </MaskedLine>
        <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.15}>
            <span className="block text-5xl font-light sm:text-6xl lg:text-7xl">
              Worn like a <span className="italic text-[#D4AF37]">manifesto</span>
            </span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60">
            Every piece is made to order and fulfilled through our Etsy studio. No checkout here. The art leads, the
            link follows.
          </p>
        </Reveal>
      </header>

      <section className="mt-20" data-testid="shop-apparel">
        <Reveal>
          <p className={eyebrow}>Apparel</p>
          <h2 className="mt-3 font-serif text-3xl font-light md:text-4xl">Wear the work</h2>
        </Reveal>
        <div className="mt-12 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          {products
            .filter((p) => p.kind !== "print")
            .map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <ProductCard product={p} />
              </Reveal>
            ))}
        </div>
      </section>

      {products.some((p) => p.kind === "print") && (
        <section className="mt-28 border-t border-white/10 pt-20" data-testid="shop-prints">
          <Reveal>
            <p className={eyebrow}>Prints</p>
            <h2 className="mt-3 font-serif text-3xl font-light md:text-4xl">
              Own the <span className="italic text-[#D4AF37]">original</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/50">
              Gallery pieces as museum-grade prints. The same artwork from the wall, for your wall.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {products
              .filter((p) => p.kind === "print")
              .map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
          </div>
        </section>
      )}

      {loaded && products.length === 0 && (
        <p className="mt-20 text-white/40" data-testid="shop-empty">
          The loom is quiet. New pieces are being threaded. Check back soon.
        </p>
      )}
    </main>
  );
}
